/**
 * Follow Suggestions — surfaces profiles a user might want to follow.
 * Priority: same-gym members (not already followed), then recently active users.
 */

import { supabase } from './supabase';
import { createLogger } from './logger';
import { getFollowingIds } from './followSystem';

const log = createLogger('FOLLOW_SUGGESTIONS');

export interface SuggestedProfile {
  id: string;
  handle: string;
  displayName: string;
  avatarUrl: string | null;
}

/**
 * Get follow suggestions for a user.
 * 1. Same gym members not already followed
 * 2. Users with recent posts (last 14 days)
 * Returns up to `limit` results.
 */
export async function getFollowSuggestions(
  profileId: string,
  gymId?: string | null,
  limit = 10
): Promise<SuggestedProfile[]> {
  try {
    const followingIds = await getFollowingIds(profileId);
    const excludeIds = [profileId, ...followingIds];

    const suggestions: SuggestedProfile[] = [];
    const seenIds = new Set<string>();

    // 1. Same-gym members
    if (gymId) {
      const { data: gymMembers } = await supabase
        .from('profiles')
        .select('id, handle, display_name, avatar_url')
        .eq('home_gym_id', gymId)
        .not('id', 'in', `(${excludeIds.join(',')})`)
        .limit(limit);

      for (const m of gymMembers ?? []) {
        if (!seenIds.has(m.id)) {
          seenIds.add(m.id);
          suggestions.push({
            id: m.id,
            handle: m.handle,
            displayName: m.display_name ?? m.handle ?? 'Lifter',
            avatarUrl: m.avatar_url,
          });
        }
      }
    }

    // 2. Recently active users (posted in last 14 days)
    if (suggestions.length < limit) {
      const twoWeeksAgo = new Date(Date.now() - 14 * 86400000).toISOString();
      const remaining = limit - suggestions.length;
      const allExclude = [...excludeIds, ...Array.from(seenIds)];

      const { data: activePosts } = await supabase
        .from('posts')
        .select('user_id, profiles!posts_user_id_fkey(id, handle, display_name, avatar_url)')
        .gte('created_at', twoWeeksAgo)
        .not('user_id', 'in', `(${allExclude.join(',')})`)
        .order('created_at', { ascending: false })
        .limit(remaining * 3); // over-fetch to dedupe

      for (const p of activePosts ?? []) {
        if (suggestions.length >= limit) break;
        const prof = p.profiles as any;
        if (!prof || seenIds.has(prof.id)) continue;
        seenIds.add(prof.id);
        suggestions.push({
          id: prof.id,
          handle: prof.handle,
          displayName: prof.display_name ?? prof.handle ?? 'Lifter',
          avatarUrl: prof.avatar_url,
        });
      }
    }

    return suggestions;
  } catch (err) {
    log.error('getFollowSuggestions error:', err);
    return [];
  }
}
